package org.matthewo.components

import emotion.react.css
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.hooks.useStatefulRef
import org.matthewo.styles.TITLE_FONT_COLOR
import react.*
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import web.cssom.*
import web.html.HTMLButtonElement
import web.html.HTMLDivElement
import kotlin.math.roundToInt
import kotlin.time.Duration

external interface SliderProps : Props {
    var initial: Number?
    var min: Number?
    var max: Number?
    var steps: Int?
    var onChange: ((Double) -> Unit)?
    var dragTimeout: Duration?
}

val Slider = FC<SliderProps>("Slider") { props ->
    val scope = useCoroutineScope()

    val min = props.min?.toDouble() ?: 0.0
    val max = props.max?.toDouble() ?: 1.0
    val steps = props.steps?.let { if (it <= 0) null else it }
    val stepSize = steps?.let { (max - min) / it }

    var value by useState {
        if (null == props.min || null == props.max) return@useState 0.0
        val v = props.initial?.toDouble()?.coerceIn(min, max) ?: return@useState 0.0
        if (null == stepSize) v else (v / stepSize).roundToInt() * stepSize
    }
    val (trackRef, trackRefCallback) = useStatefulRef<HTMLDivElement>(null)
    val (handleRef, handleRefCallback) = useStatefulRef<HTMLButtonElement>(null)
    val draggingRef = useRef(false)

    val dragTimeout = props.dragTimeout
    var dragTimeoutJob by useState<Job>()

    fun updateFromPointer(clientX: Double, finished: Boolean = false) {
        val track = trackRef ?: return
        val handle = handleRef ?: return
        val rect = track.getBoundingClientRect()
        val handleWidth = handle.getBoundingClientRect().width
        val percent = (clientX - rect.left - handleWidth / 2) / (rect.width - handleWidth)
        val v = percent.coerceIn(0.0, 1.0) * (max - min) + min
        val new = if (null == stepSize) v else (v / stepSize).roundToInt() * stepSize
        dragTimeoutJob?.cancel()
        value = new
        if (finished) props.onChange?.invoke(new)
        else if (null != dragTimeout) dragTimeoutJob = scope.launch {
            delay(dragTimeout)
            props.onChange?.invoke(value)
        }
    }

    val handlePercent = useMemo(min, max, value, trackRef, handleRef) {
        if (min == max) return@useMemo 0.0
        val track = trackRef ?: return@useMemo 0.0
        val handle = handleRef ?: return@useMemo 0.0
        val trackWidth = track.getBoundingClientRect().width
        val handleWidth = handle.getBoundingClientRect().width
        val percent = 100 * ((value - min) / (max - min)).coerceIn(0.0, 1.0)
        percent * (1 - handleWidth / trackWidth)
    }

    val lineWidth = 0.05.em
    div {
        ref = trackRefCallback
        css {
            position = Position.relative
            margin = array(0.em, 1.em)
            fontSize = 1.5.rem
            height = 1.em
            border = Border(lineWidth, LineStyle.solid, TITLE_FONT_COLOR)
            borderRadius = 50.vh
            touchAction = None.none
            userSelect = None.none
            pointerEvents = Auto.auto
        }
        onPointerDown = { event ->
            event.preventDefault()
            event.currentTarget.focus()
            event.currentTarget.setPointerCapture(event.pointerId)
            draggingRef.current = true
            updateFromPointer(event.clientX)
        }
        onPointerUp = { event ->
            draggingRef.current = false
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId)
                updateFromPointer(event.clientX, true)
            }
        }
        onPointerCancel = { event ->
            draggingRef.current = false
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId)
                updateFromPointer(event.clientX, true)
            }
        }
        onPointerMove = { event ->
            if (draggingRef.current == true) updateFromPointer(event.clientX)
        }

        if (null != steps) {
            (0..steps).forEach { step ->
                div {
                    css {
                        position = Position.absolute
                        top = 0.em
                        left = (step.toDouble() / steps) * (100.pct - 1.em) + 0.5.em
                        width = lineWidth
                        height = 100.pct
                        background = TITLE_FONT_COLOR
                        transform = translate((-50).pct)
                        pointerEvents = None.none
                    }
                }
            }
        }

        button {
            ref = handleRefCallback
            css {
                display = Display.block
                height = 1.em
                width = 1.em
                position = Position.relative
                background = TITLE_FONT_COLOR
                border = None.none
                borderRadius = 50.pct
                fontSize = 1.em
                left = handlePercent.pct
            }
        }
    }
}
